import React from "react";
import { Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { getDollarValue } from "../../helpers/currency";
import { getCogs } from "../../tables/QuoteCalcTable/helper";
import useQuoteEntities from "../../tables/QuoteCalcTable/useQuoteEntities";

import { BasicCardStack, StyledQuoteRow } from "./styles";

const margins = [0.6, 0.5, 0.4];
const marginLabels = [40, 50, 60];

const MARGIN_COST_KEYS = [
  "heatTreat",
  "machining",
  "destructive",
  "xray",
  "certifications",
  "international",
  "other",
  "comission",
];

const getMarket = (cost, weight) =>
  cost / (1 - (0.716 - 0.0433 * Math.log(weight)));

const computeImpliedMargin = (total, cost) => {
  if (!total || total <= 0 || !cost || cost <= 0) return null;
  return ((total.toFixed(2) - cost.toFixed(2)) / total.toFixed(2)) * 100;
};

const QuoteMargins = () => {
  const { watch } = useFormContext();
  const entities = useQuoteEntities();

  const weight = watch("weight") || 1;

  const cogs = getCogs(watch, entities);
  const total = Number(watch("total")) || 0;

  const extraCosts = MARGIN_COST_KEYS.reduce(
    (sum, key) => sum + (Number(watch(`cost.${key}`)) || 0),
    0,
  );
  const marginBase = cogs + extraCosts;

  const domesticMatrix = margins.map((n) => marginBase / n);
  const impliedMargin = computeImpliedMargin(total, marginBase);

  return (
    <BasicCardStack
      sx={{ padding: 0, gap: 0, flexGrow: 0 }}
      justifyContent="space-between"
      height="fit-content"
    >
      <Stack>
        <StyledQuoteRow header includePadding>
          <Typography variant="caption">Gross Margin</Typography>
          <Typography variant="caption">40%</Typography>
          <Typography variant="caption">50%</Typography>
          <Typography variant="caption">60%</Typography>
        </StyledQuoteRow>

        <StyledQuoteRow sx={{ backgroundColor: "hubColors.mainFocusLighter" }}>
          <Typography variant="caption">Domestic Cast Price</Typography>
          {domesticMatrix?.map((c, i) => (
            <Typography variant="caption" key={i}>
              {getDollarValue(c)}
            </Typography>
          ))}
        </StyledQuoteRow>

        <StyledQuoteRow>
          <Typography variant="caption">Price / Lbs</Typography>
          {domesticMatrix?.map((c, i) => (
            <Typography variant="caption" key={i}>
              {getDollarValue(c / weight)}
            </Typography>
          ))}
        </StyledQuoteRow>

        <StyledQuoteRow>
          <Typography variant="caption">Market Standard Price</Typography>
          {domesticMatrix?.map((_, i) => (
            <Typography variant="caption" key={i}>
              {getDollarValue(getMarket(marginBase, weight))}
            </Typography>
          ))}
        </StyledQuoteRow>

        <StyledQuoteRow
          removeBorder
          sx={{ backgroundColor: "hubColors.greyLightest" }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Quote Total
          </Typography>
          {marginLabels.map((m, i) => {
            const isClosest =
              impliedMargin != null &&
              marginLabels.reduce((best, cur) =>
                Math.abs(cur - impliedMargin) < Math.abs(best - impliedMargin)
                  ? cur
                  : best,
              ) === m;
            return (
              <Typography
                key={i}
                variant="caption"
                sx={{
                  fontWeight: isClosest ? 700 : 400,
                  color: isClosest ? "hubColors.mainFocus" : "text.secondary",
                }}
              >
                {isClosest && total > 0
                  ? `${getDollarValue(total)}${
                      impliedMargin != null
                        ? ` (${impliedMargin.toFixed(1)}%)`
                        : ""
                    }`
                  : "—"}
              </Typography>
            );
          })}
        </StyledQuoteRow>
      </Stack>
    </BasicCardStack>
  );
};

export default QuoteMargins;
